/**
 * The data area's typed edge.
 *
 * One Zod schema per `/read/*` response body (platform design §12.2, §6.3.9).
 * `handlers.ts` is a local fake of those routes, and every request goes through
 * `client.getParsed` with one of these before it renders: a field the real
 * route renames surfaces as a parse failure, not a blank cell.
 *
 * Where a `contract/*.schema.json` file exists it is the authority and
 * `contract.test.ts` re-derives a schema from it and compares (`api`
 * `tests/test_contract_equality.py` pins the same vendored files against the response
 * models, byte for byte). `postSchema` and `digestSchema` stay ASSUMED: their
 * routes are real (`api` `read_post`, `read_post_digest`) but carry no
 * independently vendored contract (`NOT_YET_SCHEMA_BACKED` in that test).
 * `overviewSchema` has a real, tested producer (`overview.producer-schema.json`)
 * for every field but `territory`, which the route never returns — see
 * `rankRowSchema`.
 *
 * Every nullable field here was diffed against its producer column on core
 * `origin/main` (`[zod-nullability-against-producer-and-scope]`):
 * `metric_observations.value` is nullable, `provider` is not;
 * `amplification_edges.provider` and `confidence` are nullable;
 * `accounts.current_handle` is nullable (the amplifier handle is looked up
 * through it); `tracked_accounts.influence_scope` is nullable. `media.poster_url`,
 * `media.duration_seconds` and `postRowSchema.avatar_url` are nullable the same
 * way `source_url` is: the stored file, or the account's picture, may never
 * have been captured.
 */

import { z } from 'zod';

const iso = z.iso.datetime({ offset: true });

export const fidelitySchema = z.enum(['MEASURED', 'ESTIMATED']);

export const postMetricRowSchema = z.object({
  post_id: z.uuid(),
  metric: z.enum(['views', 'likes', 'comments', 'shares', 'saves', 'estimated_reach']),
  observed_at: iso,
  value: z.number().int().nullable(),
  provider: z.string(),
  fidelity: fidelitySchema,
});
export type PostMetricRow = z.infer<typeof postMetricRowSchema>;

export const postMetricsSchema = z.object({ rows: z.array(postMetricRowSchema) });
export type PostMetrics = z.infer<typeof postMetricsSchema>;

export const amplificationRowSchema = z.object({
  campaign_id: z.uuid(),
  source_post_id: z.uuid(),
  amplifier_account_id: z.uuid(),
  amplifier_handle: z.string().nullable(),
  kind: z.enum(['STORY_SHARE', 'REPOST', 'AUDIO_REUSE', 'COMMENT', 'LIKE']),
  method: z.enum([
    'FINGERPRINT_PHASH',
    'FINGERPRINT_ORB',
    'ACCOUNT_ID_MATCH',
    'HANDLE_MATCH',
    'AUDIO_ID_MATCH',
  ]),
  confidence: z.number().nullable(),
  provider: z.string().nullable(),
  observed_at: iso,
  /** The amplifier's typical reach over the window, the figure share reach sums. */
  reach: z.number().int().nullable(),
});
export type AmplificationRow = z.infer<typeof amplificationRowSchema>;

export const amplificationSchema = z.object({ rows: z.array(amplificationRowSchema) });
export type Amplification = z.infer<typeof amplificationSchema>;

export const postRowSchema = z.object({
  post_id: z.uuid(),
  account_id: z.uuid(),
  handle: z.string().nullable(),
  label: z.string().nullable(),
  platform: z.string(),
  kind: z.string(),
  published_at: iso,
  url: z.string().nullable(),
  caption: z.string().nullable(),
  deleted_at: iso.nullable(),
  media: z.array(
    z.object({
      ordinal: z.number().int(),
      /** What the stored file is, never the provider's own POST/REEL/STORY kind. */
      media_kind: z.enum(['image', 'video']),
      /** A signed S3 URL, 1h expiry; null if the file was never stored. */
      source_url: z.string().nullable(),
      /** True while `source_url` is null because the file has not been
       *  downloaded yet -- never for the platform's own expiring CDN link,
       *  which this API never hands out. */
      pending: z.boolean(),
      /** A video's cover frame (signed); null for an image or a video with no cover. */
      poster_url: z.string().nullable(),
      duration_seconds: z.number().nullable(),
    }),
  ),
  /** The author's stored profile picture (signed); null if never stored. */
  avatar_url: z.string().nullable(),
  /** Collab posts' other authors. */
  coauthors: z.array(
    z.object({
      account_id: z.uuid(),
      handle: z.string().nullable(),
      avatar_url: z.string().nullable(),
    }),
  ),
  related: z.boolean(),
  /** When a sweep first saw the story; null for a post or reel. */
  captured_at: iso.nullable(),
  /** `published_at` + 24h; null for a post or reel. */
  expires_at: iso.nullable(),
  /** An S3 key; the UI never renders it directly. */
  thumbnail_ref: z.string().nullable(),
  /** Every sweep that saw the story, oldest first; null for a post or reel. */
  captures: z.array(iso).nullable(),
});
export type PostRow = z.infer<typeof postRowSchema>;

/** `related` and `total` count the whole window, not the rows returned under
 *  `limit` or the `related` filter, so the Recorte pill reads both off any page. */
export const postsSchema = z.object({
  rows: z.array(postRowSchema),
  /** Opens the next page; null on the last. */
  next_cursor: z.string().nullable().default(null),
  related: z.number().int(),
  total: z.number().int(),
});
export type Posts = z.infer<typeof postsSchema>;

/** `overridden` when somebody reclassified the post by hand, `derived` otherwise. */
export const relatedProvenanceSchema = z.enum(['derived', 'overridden']);
export type RelatedProvenance = z.infer<typeof relatedProvenanceSchema>;

/** An estimate: `value` and `confidence` are null when an input is missing,
 *  and the method version stays either way. */
export const shareReachSchema = z.object({
  value: z.number().int().nullable(),
  method_version: z.string(),
  confidence: z.number().nullable(),
});
export type ShareReach = z.infer<typeof shareReachSchema>;

/** `get_post_reach`. An unrelated post has no reach here, not a zero, and
 *  `reach_delta` is a ratio (`0.084` is +8.4%). */
export const postReachSchema = z.object({
  post_id: z.uuid(),
  campaign_id: z.uuid(),
  related: z.boolean(),
  related_provenance: relatedProvenanceSchema,
  reach: z.number().int().nullable(),
  previous_reach: z.number().int().nullable(),
  reach_delta: z.number().nullable(),
  share_reach: shareReachSchema,
});
export type PostReach = z.infer<typeof postReachSchema>;

/** ASSUMED: `api`'s `read_post` route is real but `PostOut` has no vendored
 *  contract file (`NOT_YET_SCHEMA_BACKED`, `api` `tests/test_contract_equality.py`).
 *  `figures` is `get_post_reach` over the requested window, null exactly when
 *  `post` is — and always null for a story, which carries no figures. */
export const postSchema = z.object({
  post: postRowSchema.nullable(),
  figures: postReachSchema.nullable(),
});
export type Post = z.infer<typeof postSchema>;

/** ASSUMED: the effective flag the relatedness write route answers with. */
export const relatednessSchema = z.object({
  related: z.boolean(),
  related_provenance: relatedProvenanceSchema,
});
export type Relatedness = z.infer<typeof relatednessSchema>;

/** `get_composition` breaks a post's audience down by stance as well as by
 *  attribute, so each cell carries the stance it tallies. */
const compositionCellSchema = z.object({
  attributes: z.record(z.string(), z.string()),
  stance: z.string(),
  count: z.number(),
});

export const compositionSchema = z.object({
  cells: z.array(compositionCellSchema),
  suppressed_share: z.number(),
  total: z.number().nullable(),
  k: z.object({ default: z.number().int(), sensitive: z.number().int() }),
});
export type Composition = z.infer<typeof compositionSchema>;

/** Who a composition counts: the people who commented, or those who liked. */
export type AudienceSource = 'commenter' | 'liker';

export const stanceSchema = z.enum(['supportive', 'hostile', 'neutral', 'off_topic']);

/** One term's uses in the window. The split adds up to `count` (`off_topic`
 *  folds into `neutral`); `stance` is the side the split leans to. */
export const commentTermRowSchema = z.object({
  term: z.string(),
  stance: stanceSchema,
  count: z.number().int(),
  supportive: z.number().int().optional(),
  neutral: z.number().int().optional(),
  hostile: z.number().int().optional(),
});
export type CommentTermRow = z.infer<typeof commentTermRowSchema>;

export const commentTermsSchema = z.object({
  rows: z.array(commentTermRowSchema),
  posts_read: z.number().int(),
});
export type CommentTerms = z.infer<typeof commentTermsSchema>;

export const commentRowSchema = z.object({
  comment_key: z.string(),
  author_handle: z.string().nullable(),
  text: z.string(),
  is_substantive: z.boolean(),
  observed_date: z.iso.date(),
});
export type CommentRow = z.infer<typeof commentRowSchema>;

/** The stance distribution over the whole window, not over the pages fetched
 *  so far, so a screen can show it above a partially loaded list. */
export const stanceCountsSchema = z.object({
  supportive: z.number().int().min(0),
  hostile: z.number().int().min(0),
  neutral: z.number().int().min(0),
  off_topic: z.number().int().min(0),
});
export type StanceCounts = z.infer<typeof stanceCountsSchema>;

export const commentsSchema = z.object({
  rows: z.array(commentRowSchema),
  next_cursor: z.string().nullable(),
  counts: stanceCountsSchema,
});
export type Comments = z.infer<typeof commentsSchema>;

/** ASSUMED: `api`'s `read_post_digest` route is real but `DigestOut` has no
 *  vendored contract file (`NOT_YET_SCHEMA_BACKED`, `api` `tests/test_contract_equality.py`). */
export const digestSchema = z.object({
  text: z.string().nullable(),
  prompt_version: z.string().nullable(),
  comments_read: z.number().int().nullable(),
  window: z.tuple([z.string(), z.string()]).nullable(),
  generated_at: iso.nullable(),
});
export type Digest = z.infer<typeof digestSchema>;

const sentimentInfluenceScopeSchema = z.object({
  scope_ref: z.uuid(),
  level: z.string().nullable(),
});

export const sentimentPointSchema = z.object({
  campaign_id: z.uuid(),
  observed_week: z.iso.date(),
  scoring_model_version: z.string(),
  score: z.number().min(-100).max(100),
  n: z.number().int(),
  confidence_interval: z.tuple([z.number(), z.number()]),
  influence_scope: sentimentInfluenceScopeSchema,
});
export type SentimentPoint = z.infer<typeof sentimentPointSchema>;

/** `useSentiment`'s own read: the route's default `bucket=week` answers with
 *  weekly points only, so this narrower schema stays the one that hook parses
 *  with. The bucketed variant below is `useSentimentSeries`'s. */
export const sentimentSchema = z.object({ points: z.array(sentimentPointSchema) });
export type Sentiment = z.infer<typeof sentimentSchema>;

export const sentimentBucketStanceCountsSchema = z.object({
  supportive: z.number().int().min(0),
  neutral: z.number().int().min(0),
  hostile: z.number().int().min(0),
});
export type SentimentBucketStanceCounts = z.infer<typeof sentimentBucketStanceCountsSchema>;

/** `bucket=day` or `bucket=window`: scored at read time, so a bucket below
 *  `K_GENERAL` withholds `score`, `confidence_interval` and `stance_counts`
 *  rather than disclosing a single commenter's own stance. */
export const sentimentBucketPointSchema = z.object({
  campaign_id: z.uuid(),
  bucket_start: z.iso.date(),
  scoring_model_version: z.string(),
  score: z.number().min(-100).max(100).nullable(),
  n: z.number().int(),
  confidence_interval: z.tuple([z.number(), z.number()]).nullable(),
  influence_scope: sentimentInfluenceScopeSchema,
  stance_counts: sentimentBucketStanceCountsSchema.nullable(),
  withheld: z.boolean(),
});
export type SentimentBucketPoint = z.infer<typeof sentimentBucketPointSchema>;

/** The route's own wire shape (`tests/contract/data/sentiment.schema.json`):
 *  every point is one of the two point shapes, since the response's actual
 *  branch depends on the `bucket` the caller asked for, not on anything the
 *  type system sees. `useSentimentSeries` is the hook that reads a caller-
 *  chosen bucket and needs this wider parse. */
export const sentimentSeriesSchema = z.object({
  points: z.array(z.union([sentimentPointSchema, sentimentBucketPointSchema])),
});
export type SentimentSeries = z.infer<typeof sentimentSeriesSchema>;
export type SentimentSeriesPoint = SentimentSeries['points'][number];

const gapSchema = z.object({
  locality_id: z.uuid(),
  locality_name: z.string(),
  kind: z.string(),
  basis: z.string(),
  engagement_share: z.number().nullable(),
  electorate: z.number().nullable(),
  electorate_share: z.number().nullable(),
  coverage_ratio: z.number().nullable(),
  staff: z.number().int().nullable(),
  staff_density: z.number().nullable(),
  is_estimate: z.boolean(),
  estimate_method_version: z.string().nullable(),
  estimate_confidence: z.number().nullable(),
  mean_overlap_ratio: z.number().nullable(),
  territory_class: z.string().nullable(),
  commenter_sample_bias_note: z.string().nullable(),
});

const gapReportSchema = z.object({
  basis: z.string(),
  gaps: z.array(gapSchema),
  polling_places_without_point: z.number().int(),
  sections_without_catchment: z.number().int(),
  electorate_without_catchment: z.number().int(),
  sections_without_electorate: z.number().int(),
  localities_unmeasured: z.number().int(),
  classification_applied: z.boolean(),
  territory_class_filter: z.string().nullable(),
  scope_restricted: z.boolean(),
});

/** One returned cell, carrying the fields `get_territory` computes for it
 *  beyond the attributes and the count (platform design §11.3). */
export const territoryCellSchema = z.object({
  attributes: z.record(z.string(), z.string().nullable()),
  count: z.number().int(),
  apportioned_count: z.number(),
  k: z.number().int(),
  generalized: z.boolean(),
  merged_bundles: z.number().int(),
  is_estimate: z.boolean(),
  estimate_method_version: z.string().nullable(),
  estimate_confidence: z.number().nullable(),
  mean_overlap_ratio: z.number().nullable(),
});
export type TerritoryCell = z.infer<typeof territoryCellSchema>;

const electorateSchema = z.object({
  registered: z.number().nullable(),
  locality_name: z.string(),
  is_estimate: z.boolean(),
  estimate_method_version: z.string().nullable(),
  estimate_confidence: z.number().nullable(),
  mean_overlap_ratio: z.number().nullable(),
  sections_without_electorate: z.number().int(),
  sections: z.number().int().optional(),
  turnout: z.number().nullable().optional(),
});

/** `get_territory`'s own return shape: the six keys it always computes are
 *  required, and every key conditioned on the requested metrics is omitted
 *  when not computed. */
export const territorySchema = z.object({
  scope_ref: z.uuid(),
  metrics: z.array(z.string()),
  classification: z.string().nullable(),
  territory_class: z.string().nullable(),
  k: z.object({ default: z.number().int(), sensitive: z.number().int() }),
  suppressed_share: z.number().nullable(),
  suppressed_count: z.number().nullable().optional(),
  suppressed_cells: z.number().int().nullable().optional(),
  generalized_share: z.number().nullable().optional(),
  total: z.number().nullable().optional(),
  cells: z.array(territoryCellSchema).optional(),
  is_estimate: z.boolean().optional(),
  estimate_method_version: z.string().nullable().optional(),
  estimate_confidence: z.number().nullable().optional(),
  electorate: electorateSchema.nullable().optional(),
  /** Each attribute's values summed across the cells, present only when nothing was withheld. */
  dimension_totals: z
    .record(
      z.string(),
      z.array(
        z.object({ value: z.string(), count: z.number().int(), share_pct: z.number().nullable() }),
      ),
    )
    .optional(),
  coverage_gaps: gapReportSchema.optional(),
  staff_coverage_gaps: gapReportSchema.optional(),
});
export type Territory = z.infer<typeof territorySchema>;

/** Reach, posts and active-accounts each carry this shape; `overviewSchema`
 *  extends it for the sentiment field, which the producer carries two more
 *  fields for (§10.5: a score is never shown without its `n` and interval). */
export const figureSchema = z.object({
  value: z.number().nullable(),
  previous: z.number().nullable(),
});
export type Figure = z.infer<typeof figureSchema>;

export const movementBucketSchema = z.object({
  bucket: iso,
  value: z.number().nullable(),
});
export type MovementBucket = z.infer<typeof movementBucketSchema>;

export const tagRowSchema = z.object({
  tag: z.string(),
  reach: z.number().int().nullable(),
  posts: z.number().int().nullable(),
});
export type TagRow = z.infer<typeof tagRowSchema>;

/** ASSUMED: the overview route's real producer returns no territory ranking at
 *  all (`api` `tests/contract/data/overview.producer-schema.json` declares no
 *  `territory` key). Owner ruling 2026-09-20
 *  (`owner-decisions-read-routes-overview-scope`) deferred it to its own queued
 *  task, not yet filed. */
export const rankRowSchema = z.object({
  locality_id: z.uuid(),
  name: z.string(),
  value: z.number().nullable(),
});
export type RankRow = z.infer<typeof rankRowSchema>;

/** Backed by a real, tested producer (`api` `read_overview`,
 *  `overview.producer-schema.json`, `OverviewOut`) for every field but
 *  `territory`, which the route never returns — see `rankRowSchema`; optional
 *  here so a real response still parses. `api` owns this contract file
 *  outright and never vendors it to `web` (`api` `tests/test_contract_equality.py`),
 *  so this schema is hand-kept against the route's response model instead of
 *  pinned by `contract.test.ts`. */
export const overviewSchema = z.object({
  reach: figureSchema,
  posts: figureSchema,
  active_accounts: figureSchema,
  sentiment: figureSchema.extend({
    n: z.number().int().nullable().optional(),
    confidence_interval: z.tuple([z.number(), z.number()]).nullable().optional(),
  }),
  movement: z.array(movementBucketSchema),
  by_tag: z.array(tagRowSchema),
  by_tag_omitted: z.number().int().nullable().optional(),
  territory: z.array(rankRowSchema).optional(),
});
export type Overview = z.infer<typeof overviewSchema>;

/** `GET /campaigns/{c}/breakdown` (`api` `routers/read_breakdown.py`,
 *  `BreakdownOut`): one figure broken down by one campaign dimension (a
 *  `TagCategory`), the overview, Território and Sentimento cards' shared
 *  read. Not under `/read/*`: the route sits beside the tag vocabulary's own
 *  `/campaigns/{c}/tags/*` rather than the eight registered tools, per that
 *  router's own docstring. `core`'s `read_breakdown` computes no account-kind
 *  distribution (owner ruling, this screen's dimension chip) and offers only
 *  views and comments — no collector writes a repost count in production. */
export const breakdownMetricSchema = z.enum(['views', 'comments']);
export type BreakdownMetric = z.infer<typeof breakdownMetricSchema>;

export const breakdownDimensionSchema = z.object({
  id: z.string(),
  name: z.string(),
});
export type BreakdownDimension = z.infer<typeof breakdownDimensionSchema>;

const breakdownDailyValueSchema = z.object({
  day: z.iso.date(),
  value: z.number().int(),
});

export const breakdownGroupSchema = z.object({
  value_id: z.string(),
  name: z.string(),
  account_count: z.number().int(),
  series: z.array(breakdownDailyValueSchema),
  total: z.number().int().nullable(),
});
export type BreakdownGroup = z.infer<typeof breakdownGroupSchema>;

export const breakdownSchema = z.object({
  dimension: breakdownDimensionSchema,
  metric: breakdownMetricSchema,
  groups: z.array(breakdownGroupSchema),
});
export type Breakdown = z.infer<typeof breakdownSchema>;

const themeFields = {
  value_id: z.uuid(),
  name: z.string(),
  comment_count: z.number().int(),
  supportive: z.number().int(),
  neutral: z.number().int(),
  hostile: z.number().int(),
  net_score: z.number().min(-100).max(100),
};

/** A Tema the campaign's comment intelligence classified comments on, with
 *  its direct sub-themes. */
export const themeRowSchema = z.object({
  ...themeFields,
  children: z.array(z.object(themeFields)),
});
export type ThemeRow = z.infer<typeof themeRowSchema>;

/** `GET /campaigns/{c}/topics` (`api` `routers/read/topics.py`): the Tema
 *  themes the campaign's comments were classified on, ranked by whichever
 *  `order` the caller asked for. A theme under the k-gate is withheld and only
 *  counted; one under three comments is folded into `others`. */
export const topicsSchema = z.object({
  themes: z.array(themeRowSchema),
  others: z.object({ topics: z.number().int(), comment_count: z.number().int() }),
  withheld: z.number().int(),
});
export type Topics = z.infer<typeof topicsSchema>;

/** `GET /campaigns/{c}/localities` (`api` `routers/read/localities.py`):
 *  reach, comment volume and score per locality under the campaign's own
 *  scope reference; the extremes are the route's own output, never picked
 *  by the caller. The route refuses a campaign with no scope reference
 *  before this shape is ever produced. */
const reachFigureSchema = z.object({
  value: z.number().int().nullable(),
  previous: z.number().int().nullable(),
  delta: z.number().int().nullable(),
});

const volumeFigureSchema = z.object({
  value: z.number().int(),
  previous: z.number().int(),
  delta: z.number().int(),
});

const localityScoreSchema = z.object({
  value: z.number().nullable(),
  previous: z.number().nullable(),
  delta: z.number().nullable(),
  n: z.number().int(),
  withheld: z.boolean(),
});

export const localityRowSchema = z.object({
  place_id: z.uuid(),
  /** What the map's tiles key a boundary by: they are shared by every
   *  organisation, so they carry no place id. */
  locality_id: z.uuid(),
  name: z.string(),
  reach: reachFigureSchema,
  comment_volume: volumeFigureSchema,
  score: localityScoreSchema,
});
export type LocalityRow = z.infer<typeof localityRowSchema>;

const extremeSchema = z.object({
  place_id: z.uuid(),
  name: z.string(),
  delta: z.number(),
});
export type Extreme = z.infer<typeof extremeSchema>;

const metricExtremesSchema = z.object({
  largest_rise: extremeSchema.nullable(),
  largest_fall: extremeSchema.nullable(),
});

const extremesSchema = z.object({
  reach: metricExtremesSchema,
  comment_volume: metricExtremesSchema,
  score: metricExtremesSchema,
});
export type Extremes = z.infer<typeof extremesSchema>;

export const localitiesSchema = z.object({
  level: z.enum(['neighborhood', 'municipality', 'state']),
  localities: z.array(localityRowSchema),
  extremes: extremesSchema,
});
export type Localities = z.infer<typeof localitiesSchema>;

/** `GET /campaigns/{c}/ranked-posts` (`api` `routers/read/ranked_posts.py`):
 *  the campaign's posts that pulled its sentiment score down, worst first,
 *  gated at `K_GENERAL` the same way a locality's score is — every row's
 *  score is a real, negative, disclosed one, never withheld or absent. */
const rankedPostScoreSchema = z.object({
  value: z.number(),
  n: z.number().int(),
  confidence_interval: z.tuple([z.number(), z.number()]),
});

export const rankedPostRowSchema = z.object({
  post_id: z.uuid(),
  handle: z.string().nullable(),
  label: z.string().nullable(),
  caption_excerpt: z.string().nullable(),
  comment_count: z.number().int(),
  score: rankedPostScoreSchema,
});
export type RankedPostRow = z.infer<typeof rankedPostRowSchema>;

export const rankedPostsSchema = z.object({ rows: z.array(rankedPostRowSchema) });
export type RankedPosts = z.infer<typeof rankedPostsSchema>;

/** `api` `read_shared_media`: every OTHER publication, from any tracked
 *  account including this one's own, whose media matches this one's -- an undirected, transitive group
 *  over the match graph, regardless of who posted or shared first. */
export const sharedMediaSchema = z.object({
  rows: z.array(
    z.object({
      /** The other publication, read on its own page. */
      post_id: z.uuid(),
      account_id: z.uuid(),
      kind: z.enum(['POST', 'REEL', 'STORY']),
      handle: z.string().nullable(),
      label: z.string().nullable(),
      /** Signed, 1h expiry; null until the account's picture is stored. */
      avatar_url: z.string().nullable(),
      published_at: z.iso.datetime({ offset: true }),
      reach: z.number().int().nullable(),
      confidence: z.number(),
      /** Published by the same account as the publication being read. */
      same_account: z.boolean(),
    }),
  ),
});
